import {
  RequirementStepsResponse,
  StepDetailsResponse
} from '../types/RequirementSteps.types';
import API from './ApiBase';

export const fetchRequirementSteps = async (
  aiSystemId: string
): Promise<RequirementStepsResponse> => {
  try {
    const response = await API.get<RequirementStepsResponse>(
      `/requirements/${aiSystemId}/steps`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching requirement steps:', error);
    throw error;
  }
};

export const fetchStepActions = async (
  stepId: string
): Promise<StepDetailsResponse> => {
  try {
    const response = await API.get<StepDetailsResponse>(
      `/requirements/${stepId}/actions`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching step actions:', error);
    throw error;
  }
};

export const setActionCompleted = async (
  stepId: string,
  actionId: string,
  completed: boolean
) => {
  try {
    const response = await API.patch(
      `/requirements/${stepId}/actions/${actionId}`,
      { completed }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating requirement action:', error);
    throw error;
  }
};

export const finishCompliance = async (aiSystemId: string) => {
  try {
    const response = await API.patch(
      `/requirements/${aiSystemId}/finish-compliance`
    );
    return response.data;
  } catch (error) {
    console.error('Error finishing compliance:', error);
  }
};

export type SubmitRequirementEvidencePayload = {
  url?: string;
  documentId?: string;
  attestationDate?: string;
};

export type SubmitRequirementEvidenceResponse = {
  actionId: string;
  completed: boolean;
};

// The evidence kind is inferred server-side from the action's stored
// `evidenceKind`; the client only sends the value for that kind.
export const submitRequirementEvidence = async (
  actionId: string,
  payload: SubmitRequirementEvidencePayload,
  // Opt-in, not baked in: the url and document controls map the failure onto the field that
  // caused it and would otherwise show the same problem twice - once inline and once as the
  // interceptor's global toast (EAA-1937). The attestation control has no inline error surface
  // at all, so it must keep the toast.
  options?: { skipErrorToast?: boolean }
): Promise<SubmitRequirementEvidenceResponse> => {
  try {
    const response = await API.post<SubmitRequirementEvidenceResponse>(
      `/requirements/actions/${actionId}/evidence`,
      payload,
      options?.skipErrorToast ? { skipErrorToast: true } : undefined
    );
    return response.data;
  } catch (error) {
    console.error('Error submitting requirement evidence:', error);
    throw error;
  }
};

export type AcknowledgeLeadTimeResponse = {
  stepId: string;
  acknowledged: boolean;
};

/**
 * Accept, or withdraw acceptance of, a notice-window breach.
 *
 * A late notice cannot be un-sent, so without this a system stays flagged with no route out.
 * Accepting lets the obligation close and clears the system's attention state — it does NOT erase
 * the breach, which stays on the step and in the audit log.
 *
 * The API refuses anything but a real, evaluated breach: a met window has nothing to accept, and an
 * unevaluated one (no first-use date) must not be acknowledged past.
 */
export const acknowledgeLeadTime = async (
  stepId: string,
  acknowledged: boolean
): Promise<AcknowledgeLeadTimeResponse> => {
  try {
    const response = await API.patch<AcknowledgeLeadTimeResponse>(
      `/requirements/${stepId}/lead-time-acknowledgement`,
      { acknowledged }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating the lead-time acknowledgement:', error);
    throw error;
  }
};
